import store from '@/store';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';

dayjs.extend(utc);
dayjs.extend(timezone);

export default {
  getDateDurationFormat(start, end, isSourceUtcTime) {
    const _start = this.getDateDotFormat(start, isSourceUtcTime);
    const _end = this.getDateDotFormat(end, isSourceUtcTime);
    return `${_start} ~ ${_end}`;
  },
  getDateDashFormat(time, format, isSourceUtcTime) {
    if (!time) return '';
    return isSourceUtcTime
      ? dayjs.utc(time).format(format ? format : 'YYYY-MM-DD')
      : dayjs(time).format(format ? format : 'YYYY-MM-DD');
  },
  getDateDotFormat(time, isSourceUtcTime) {
    if (!time) return '';
    return isSourceUtcTime
      ? dayjs.utc(time).format('YYYY-MM-DD')
      : dayjs(time).format('YYYY-MM-DD');
  },
  setTimeToUtc(time) {
    if (!time) return '';
    return dayjs(time).utc();
  },
  isSame(firTime, secTime) {
    return dayjs(firTime).isSame(secTime);
  },
  getFactoryDayStartEndTime(factoryTimezone) {
    const timeZone = factoryTimezone
      ? factoryTimezone
      : store.state.app.factory.timezone;
    const formatter = new Intl.DateTimeFormat('zh-TW', {
      timeZone,
      timeZoneName: 'shortOffset',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hourCycle: 'h23',
    });
    const date = formatter.format(new Date()).replace(/\//g, '-').replace(/\[|\]/g, '');
    const formattedDate = dayjs(date, 'YYYY-MM-DD HH:mm:ss').format('YYYY-MM-DD');
    const utcOffset = formatter.formatToParts(new Date()).filter(e => e.type === 'timeZoneName')[0].value.substring(3);
    const startOfDay = dayjs(`${formattedDate} 00:00:00`, 'YYYY-MM-DD HH:mm:ss').subtract(utcOffset, 'h').format('YYYY-MM-DD HH:mm:ss');
    const endOfDay = dayjs(`${formattedDate} 23:59:59`, 'YYYY-MM-DD HH:mm:ss').subtract(utcOffset, 'h').format('YYYY-MM-DD HH:mm:ss');
    return {start: startOfDay, end: endOfDay};
  },
  startOf(time, format, unit) {
    if (!time) return '';
    return dayjs(time)
      .startOf(unit ? unit : 'day')
      .format(format ? format : 'YYYY-MM-DD HH:mm:ss');
  },
  endOf(time, format, unit) {
    if (!time) return '';
    return dayjs(time)
      .endOf(unit ? unit : 'day')
      .format(format ? format : 'YYYY-MM-DD HH:mm:ss');
  },
  isAfter(timeA, timeB) {
    if (!timeA || !timeB) return;
    return timeA.isAfter(timeB);
  },
  addTimeZone(time, timezone) {
    if (!time) return '';
    return dayjs.tz(time, timezone || store.state.app.factory.timezone);
  },
  convertToTimeZone(time, timezone) {
    if (!time) return '';
    return dayjs(time).tz(timezone || store.state.app.factory.timezone);
  },
  convertTimeToChineseFormat(timeString) {
    if (!timeString) return '';

    const date = dayjs(timeString, 'YYYY-MM-DD HH:mm');
    if (!date.isValid()) return '';

    // 轉換年份：西元年轉民國年並轉成中文數字（每個數字單獨轉換）
    const year = date.year();
    const rocYear = year - 1911;
    const yearChinese = this.getChineseYear(rocYear);

    // 轉換月份：轉成中文月份
    const month = date.month() + 1; // dayjs month 是 0-11
    const monthChinese = this.getChineseMonth(month);

    // 轉換日期：轉成中文數字
    const day = date.date();
    const dayChinese = this.getChineseDate(day);

    // 轉換星期：轉成中文
    const weekDay = date.day(); // 0=星期日, 1=星期一, ..., 6=星期六
    const weekDayChinese = this.getChineseWeekDay(weekDay);

    // 轉換時間：12小時制，小時和分鐘用阿拉伯數字
    const hour = date.hour();
    const minute = date.minute();
    const { period, hour12 } = this.convertToChinese12Hour(hour);
    const minuteStr = String(minute).padStart(2, '0');
    const timeChinese = `${period}${hour12}點${minuteStr}分`;

    return `${yearChinese}年${monthChinese}${dayChinese}日(${weekDayChinese})${timeChinese}`;
  },
  getChineseYear(year) {
    // 年份每個數字單獨轉換，例如：114 -> 一一四
    const chineseDigits = ['零', '一', '二', '三', '四', '五', '六', '七', '八', '九'];
    return year
      .toString()
      .split('')
      .map(digit => chineseDigits[parseInt(digit)])
      .join('');
  },
  getChineseDate(num) {
    const chineseNumbers = ['', '一', '二', '三', '四', '五', '六', '七', '八', '九', '十'];

    if (num === 0) return '零';
    if (num < 11) return chineseNumbers[num];
    if (num < 20) return `十${chineseNumbers[num % 10]}`;
    if (num < 100) {
      const tens = Math.floor(num / 10);
      const ones = num % 10;
      return `${chineseNumbers[tens]}十${chineseNumbers[ones]}`;
    }
    if (num < 1000) {
      const hundreds = Math.floor(num / 100);
      const remainder = num % 100;
      const tens = Math.floor(remainder / 10);
      const ones = remainder % 10;
      let result = `${chineseNumbers[hundreds]}百`;
      if (tens > 0) {
        result += `${chineseNumbers[tens]}十`;
      } else if (ones > 0 && hundreds > 0) {
        result += '零';
      }
      if (ones > 0) {
        result += chineseNumbers[ones];
      }
      return result;
    }

    // 處理四位數
    const thousands = Math.floor(num / 1000);
    const remainder = num % 1000;
    let result = `${chineseNumbers[thousands]}千`;
    if (remainder > 0) {
      if (remainder < 100) {
        result += '零';
      }
      result += this.getChineseDate(remainder);
    }
    return result;
  },
  getChineseMonth(month) {
    const months = [
      '',
      '一月',
      '二月',
      '三月',
      '四月',
      '五月',
      '六月',
      '七月',
      '八月',
      '九月',
      '十月',
      '十一月',
      '十二月',
    ];
    return months[month] || '';
  },
  getChineseWeekDay(day) {
    const weekDays = ['星期日', '星期一', '星期二', '星期三', '星期四', '星期五', '星期六'];
    return weekDays[day] || '';
  },
  convertToChinese12Hour(hour24) {
    if (hour24 === 0) {
      return { period: '上午', hour12: 12 };
    }
    if (hour24 < 12) {
      return { period: '上午', hour12: hour24 };
    }
    if (hour24 === 12) {
      return { period: '下午', hour12: 12 };
    }
    return { period: '下午', hour12: hour24 - 12 };
  },
  isExpired(date) {
    const today = dayjs().format();
    const expired = dayjs(date).format();
    return dayjs(today).diff(dayjs(expired), 'days') > 0;
  },
}
